import { c as _c } from "react/compiler-runtime";
import { Play } from "lucide-react";
import { mediaSrc } from "@/data/archivalEventsData";

/** Collect unique images & videos for the detail page gallery */
export function buildEventMediaItems(event) {
  if (!event) return [];
  const items = [];
  const seen = new Set();
  const add = (type, src, alt) => {
    if (!src) return;
    const key = mediaSrc(src);
    if (seen.has(key)) return;
    seen.add(key);
    items.push({
      type,
      src,
      alt: alt || event.title
    });
  };
  if (event.image) add("image", event.image, event.imageAlt);
  if (event.video) add("video", event.video, `Video: ${event.title}`);
  event.gallery?.forEach(g => add(g.type, g.src, g.alt));
  return items;
}
function GalleryImage(t0) {
  const $ = _c(5);
  const {
    src,
    alt
  } = t0;
  let t1;
  if ($[0] !== src) {
    t1 = mediaSrc(src);
    $[0] = src;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== alt || $[3] !== t1) {
    t2 = <figure className="w-full overflow-hidden border border-[#0F1C2E]/10 bg-[#0F1C2E] shadow-[0_8px_32px_rgba(15,28,46,0.08)]"><img src={t1} alt={alt} className="w-full h-auto max-h-[min(85vh,720px)] object-contain object-center block mx-auto" loading="lazy" /></figure>;
    $[2] = alt;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function GalleryVideo(t0) {
  const $ = _c(13);
  const {
    src,
    index
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="w-11 h-11 shrink-0 rounded-full border border-[#DF6D2A]/50 flex items-center justify-center text-[#DF6D2A]"><Play size={18} fill="currentColor" className="ml-0.5" /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = index > 0 ? index + 1 : "";
  let t3;
  if ($[1] !== t2) {
    t3 = <p className="font-body text-[9px] tracking-[0.22em] uppercase text-[#C9A84C] mb-1.5">Event Recording {t2}</p>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="font-display text-[20px] sm:text-[24px] text-[#F4EFE6] leading-tight">Watch the ceremony</p>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3) {
    t5 = <div className="flex items-center gap-4 px-5 sm:px-8 py-6 sm:py-8 border-b md:border-b-0 md:border-r border-white/10 bg-[#0F1C2E]">{t1}<div>{t3}{t4}</div></div>;
    $[4] = t3;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== src) {
    t6 = mediaSrc(src);
    $[6] = src;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <div className="flex items-center justify-center bg-black p-3 sm:p-4 min-h-[280px] md:min-h-[360px]"><video src={t6} controls={true} playsInline={true} preload="metadata" className="w-full max-h-[min(75vh,640px)] h-auto object-contain" /></div>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t5 || $[11] !== t7) {
    t8 = <figure className="w-full overflow-hidden border border-[#0F1C2E]/10 bg-[#0F1C2E] shadow-[0_12px_40px_rgba(15,28,46,0.14)]"><div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(280px,420px)] lg:grid-cols-[1fr_400px]">{t5}{t7}</div></figure>;
    $[10] = t5;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}
export default function EventGallery(t0) {
  const $ = _c(16);
  const {
    event
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== event) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const items = buildEventMediaItems(event);
      if (!items.length) {
        t6 = null;
        break bb0;
      }
      const images = items.filter(_temp);
      const videos = items.filter(_temp2);
      const multiImages = images.length > 1;
      t4 = "mt-14 sm:mt-20 pt-12 border-t border-[#0F1C2E]/10 w-full";
      let t7;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <h2 className="font-display text-[28px] sm:text-[36px] md:text-[40px] text-[#0F1C2E] leading-none">{"Gallery & "}<em className="text-[#C9A84C] italic">Media</em></h2>;
        $[7] = t7;
      } else {
        t7 = $[7];
      }
      t5 = <div className="flex items-end justify-between gap-4 mb-8 sm:mb-10">{t7}<span className="font-body text-[10px] tracking-[0.2em] uppercase text-[#4C596B] shrink-0 hidden sm:inline">{items.length} {items.length === 1 ? "item" : "items"}</span></div>;
      t1 = "flex flex-col gap-6 sm:gap-8 w-full";
      t2 = images.length > 0 && <div className={multiImages ? "grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" : "w-full"}>{images.map((item, idx) => <GalleryImage key={mediaSrc(item.src)} src={item.src} alt={item.alt || `${event.title}, photo ${idx + 1}`} />)}</div>;
      t3 = videos.map((item_0, idx_0) => <GalleryVideo key={mediaSrc(item_0.src)} src={item_0.src} index={videos.length > 1 ? idx_0 : 0} />);
    }
    $[0] = event;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t7 = <div className={t1}>{t2}{t3}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t4 || $[13] !== t5 || $[14] !== t7) {
    t8 = <section className={t4}>{t5}{t7}</section>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
function _temp2(i_0) {
  return i_0.type === "video";
}
function _temp(i) {
  return i.type === "image";
}